/**
 * Examples of `FieldDefV1` and `LinkTypeV1` (1.0b deliverable 16a): one per shape migration 0005
 * seeds, as the built-in rows stand in a fresh database. Their ids are the seeded ones (1.0a's
 * `builtinId`, written out in `packages/db/migrations/0005_builtin_seeds.sql`); the spec
 * `field-link-defs.test.ts` proves each example equals its seeded row.
 */
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
import { EXAMPLE_TYPE_IDS } from './examples-docs.js';
/** The built-in script type, which holds the seeded fields. */
const SCRIPT_TYPE_ID = 'a1c8d964-7d93-5b5f-827a-a7c10f5c65c4';
/** The built-in `set_in` link type, which the script's Location field reads. */
const SET_IN_ID = '9bae0e6b-6fbe-520c-b6fd-6e86afa489ea';
const field = {
    docTypeId: SCRIPT_TYPE_ID,
    section: null,
    temporal: false,
    aiVisible: true,
    archived: false,
};
/** The script type's Chapter: a select with no option yet. */
export const EXAMPLE_FIELD_CHAPTER = {
    ...field,
    id: '81a10917-e4d7-50ec-b583-f8cb8e9ea754',
    key: 'chapter',
    label: 'Chapter',
    sortKey: 'a0',
    kind: 'select',
    config: { options: [] },
    linkTypeId: null,
};
/** The script type's Current?: a boolean. */
export const EXAMPLE_FIELD_CURRENT = {
    ...field,
    id: '177e049f-7d8a-5852-8115-346e5efe938e',
    key: 'current',
    label: 'Current?',
    sortKey: 'a1',
    kind: 'boolean',
    config: {},
    linkTypeId: null,
};
/** The script type's Main or Side: a select with two options. */
export const EXAMPLE_FIELD_MAIN_OR_SIDE = {
    ...field,
    id: 'cc6530b5-2298-5fb2-84a5-61f6f67e425b',
    key: 'main_or_side',
    label: 'Main or Side',
    sortKey: 'a2',
    kind: 'select',
    config: { options: ['Main', 'Side'] },
    linkTypeId: null,
};
/** The script type's Location: a document reference reading `set_in` links out of the script. */
export const EXAMPLE_FIELD_LOCATION = {
    ...field,
    id: '47ff4986-43ad-51e1-8f08-49e618a9ce80',
    key: 'location',
    label: 'Location',
    sortKey: 'a3',
    kind: 'doc_refs',
    config: { direction: 'out' },
    linkTypeId: SET_IN_ID,
};
export const EXAMPLE_FIELD_DEFS = [
    EXAMPLE_FIELD_CHAPTER,
    EXAMPLE_FIELD_CURRENT,
    EXAMPLE_FIELD_MAIN_OR_SIDE,
    EXAMPLE_FIELD_LOCATION,
];
/** `contains`: structural and ordered, with no meta key. */
export const EXAMPLE_LINK_TYPE_CONTAINS = {
    id: '49271519-e805-58ab-82b9-4cff5493d260',
    key: 'contains',
    label: 'Contains',
    inverseLabel: 'Part of',
    sourceRule: {},
    targetRule: {},
    ordered: true,
    symmetric: false,
    structural: true,
    temporal: false,
    metaSchema: { type: 'object', properties: {}, required: [], additionalProperties: false },
    builtin: true,
    archived: false,
};
/** `includes_creature`: its meta requires an integer `count` of at least 1. */
export const EXAMPLE_LINK_TYPE_INCLUDES_CREATURE = {
    id: 'f9aeb55b-5aef-539c-88b7-64ee62b78f11',
    key: 'includes_creature',
    label: 'Includes creature',
    inverseLabel: 'In encounter',
    sourceRule: {},
    targetRule: {},
    ordered: false,
    symmetric: false,
    structural: false,
    temporal: false,
    metaSchema: {
        type: 'object',
        properties: { count: { type: 'integer', minimum: 1 } },
        required: ['count'],
        additionalProperties: false,
    },
    builtin: true,
    archived: false,
};
export const EXAMPLE_LINK_TYPES = [
    EXAMPLE_LINK_TYPE_CONTAINS,
    EXAMPLE_LINK_TYPE_INCLUDES_CREATURE,
];
/*
 * The exit-1a type, field, retype and quarantine examples (1.0b deliverables 17 and 18), on the
 * Deity type of `p1a` and its fields, the NPCs' Loyalty and Old Sef's quarantined value.
 */
const deity = EXAMPLE_IDS.typeDeity.id;
const deityField = { docTypeId: deity, section: null, temporal: false, aiVisible: true };
/** Deity's Domain: a select of four options. */
export const EXAMPLE_FIELD_DOMAIN = {
    ...deityField,
    id: EXAMPLE_IDS.fieldDeityDomain.id,
    key: 'domain',
    label: 'Domain',
    sortKey: 'a0',
    kind: 'select',
    config: { options: ['Tempest', 'Death', 'Trickery', 'Nature'] },
    linkTypeId: null,
    archived: false,
};
/** Deity's Holy Symbol: text. */
export const EXAMPLE_FIELD_HOLY_SYMBOL = {
    ...deityField,
    id: EXAMPLE_IDS.fieldDeityHolySymbol.id,
    key: 'holy_symbol',
    label: 'Holy Symbol',
    sortKey: 'a1',
    kind: 'text',
    config: {},
    linkTypeId: null,
    archived: false,
};
/** The built-in `member_of` link type, which Deity's Worshippers reads inbound. */
const MEMBER_OF_ID = 'c4d0af4e-ba80-57f8-9ef2-a0a74021782f';
/** Deity's Worshippers: the documents linked to it by `member_of`. */
export const EXAMPLE_FIELD_WORSHIPPERS = {
    ...deityField,
    id: EXAMPLE_IDS.fieldDeityWorshippers.id,
    key: 'worshippers',
    label: 'Worshippers',
    sortKey: 'a2',
    kind: 'doc_refs',
    config: { direction: 'in' },
    linkTypeId: MEMBER_OF_ID,
    archived: false,
};
/** Deity's Epithet: text, archived. */
export const EXAMPLE_FIELD_EPITHET = {
    ...EXAMPLE_FIELD_HOLY_SYMBOL,
    id: EXAMPLE_IDS.fieldDeityEpithet.id,
    key: 'epithet',
    label: 'Epithet',
    sortKey: 'a3',
    archived: true,
};
/** The field `fields.create`'s example makes on Deity. */
export const EXAMPLE_FIELD_CREATED = {
    ...EXAMPLE_FIELD_HOLY_SYMBOL,
    id: EXAMPLE_ANSWER_IDS.fieldCreated,
    key: 'sacred_animal',
    label: 'Sacred Animal',
    sortKey: 'a4',
};
/** The Deity type as `types.list` answers it: one document, the Drowned Queen. */
export const EXAMPLE_DOC_TYPE = {
    id: deity,
    key: 'deity',
    name: 'Deity',
    namePlural: 'Deities',
    icon: 'deity',
    color: 'deity',
    library: 'canon',
    capabilities: ['speaker'],
    archived: false,
    builtin: false,
    sortKey: 'aG',
    foundryKind: 'JournalEntry',
    defaultTemplateId: null,
    schemaRev: 1,
    fields: [
        EXAMPLE_FIELD_DOMAIN,
        EXAMPLE_FIELD_HOLY_SYMBOL,
        EXAMPLE_FIELD_WORSHIPPERS,
        EXAMPLE_FIELD_EPITHET,
    ],
    documentCount: 1,
};
/** The type `types.create`'s example makes. */
export const EXAMPLE_DOC_TYPE_CREATED = {
    ...EXAMPLE_DOC_TYPE,
    id: EXAMPLE_ANSWER_IDS.typeCreated,
    key: 'sea_shrine',
    name: 'Sea Shrine',
    namePlural: 'Sea Shrines',
    icon: 'shrine',
    color: 'shrine',
    capabilities: ['place', 'discoverable'],
    sortKey: 'aH',
    foundryKind: null,
    fields: [],
    documentCount: 0,
};
/** Loyalty from number to text: Captain Orla Venn's 7 converts. */
export const EXAMPLE_CONVERSION_REPORT = {
    fieldDefId: EXAMPLE_IDS.fieldNpcLoyalty.id,
    fromKind: 'number',
    toKind: 'text',
    counts: { kept: 0, converted: 1, quarantined: 0 },
    optionsAdded: [],
    samples: [
        { documentId: EXAMPLE_IDS.npcOrlaVenn.id, outcome: 'converted', before: '7', after: '7' },
    ],
};
/** The Tide Mother from a note to a Deity: nothing to map, and the type gains `speaker`. */
export const EXAMPLE_RETYPE_REPORT = {
    fromTypeId: EXAMPLE_TYPE_IDS.note,
    toTypeId: deity,
    fields: [],
    links: { repointed: 0, detached: 0 },
    placements: { repointed: 0, detached: 0 },
    statesRepointed: 0,
    statesKept: 0,
    capabilities: { lost: [], gained: ['speaker'] },
    quarantined: 0,
    metaVersion: 2,
};
/** Old Sef's "about forty", set aside when Loyalty became a number. */
export const EXAMPLE_QUARANTINED_VALUE = {
    fieldDefId: EXAMPLE_IDS.fieldNpcLoyalty.id,
    id: EXAMPLE_IDS.quarantineOldSefLoyalty.id,
    value: 'about forty',
    text: 'about forty',
    fieldKey: 'loyalty',
    fieldLabel: 'Loyalty',
    kind: 'text',
    reason: 'conversion',
    fromTypeId: null,
    at: EXAMPLE_NOW,
};
//# sourceMappingURL=examples-types.js.map